import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Github, Headset, Link2, ListChecks, Loader2, MapPin, Plus, Printer, X } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { canCreateMission, statusLabel as missionStatusLabel } from "../../lib/missions";
import { ISSUE_STATUS_META, SEVERITY_META, canSeeEquipmentPage } from "../../lib/equipment";
import { TASK_STATUS_LABELS } from "../../lib/tasks";
import {
  LINK_KIND_LABELS, SATISFACTION_LABELS, TICKET_HANDLER_KEYS, formatDateTime, githubLabel, isGithubUrl, linkCount, ticketErrorMessage,
} from "../../lib/tickets";
import TaskFormSheet from "../TaskFormSheet";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";

const KIND_ICONS = { task: ListChecks, mission: MapPin, equipment_issue: Printer, assist: Headset, github: Github };

function DialogFooterButtons({ busy, error, onCancel, onSubmit, disabled, label, testid }) {
  return (
    <>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="ticket-dialog-error">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button onClick={onSubmit} disabled={busy || disabled} data-testid={testid}>
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

// What one linked object looks like: its own words, a link to its own page — or « non accessible » when the reader cannot see it.
function LinkRow({ kind, item, canRemove, onRemove, removing }) {
  const Icon = KIND_ICONS[kind];
  let title = null;
  let meta = "";
  let to = null;
  let href = null;
  if (kind === "github") {
    title = githubLabel(item.url);
    href = item.url;
  } else if (kind === "assist") {
    title = `Assistance du ${formatDateTime(item.created_at)}`;
    meta = item.ended_at ? `terminée le ${formatDateTime(item.ended_at)}` : "";
    to = `/assistance/${item.id}`;
  } else if (item.visible === false) {
    title = <span className="text-slate-400">Non accessible avec vos droits</span>;
  } else if (kind === "task") {
    title = item.title;
    meta = TASK_STATUS_LABELS[item.status] || "";
    to = { pathname: "/tasks", state: { openTask: item.id } };
  } else if (kind === "mission") {
    title = `${item.reference} · ${item.title}`;
    meta = missionStatusLabel(item.status);
    to = `/missions/${item.id}`;
  } else if (kind === "equipment_issue") {
    title = `${item.number} · ${item.title}`;
    meta = [SEVERITY_META[item.severity]?.label, ISSUE_STATUS_META[item.status]?.label].filter(Boolean).join(" · ");
    to = `/equipements?anomalie=${item.id}`;
  }
  const ref = kind === "github" ? item.url : item.id;
  return (
    <li className="flex items-start gap-2 py-2 text-sm" data-testid={`ticket-link-${kind}`}>
      <Icon size={15} className="mt-0.5 text-slate-400 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-2xs uppercase tracking-wide text-slate-400">{LINK_KIND_LABELS[kind]}</div>
        {to ? (
          <Link to={typeof to === "string" ? to : to.pathname} state={typeof to === "string" ? undefined : to.state}
            className="text-jamora hover:underline break-words">{title}</Link>
        ) : href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-jamora hover:underline break-all">
            {title}<ExternalLink size={12} aria-hidden="true" />
          </a>
        ) : <span className="break-words">{title}</span>}
        {meta && <div className="text-xs text-slate-500">{meta}</div>}
      </div>
      {canRemove && (
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={`Retirer le lien : ${LINK_KIND_LABELS[kind]}`}
          onClick={() => onRemove(kind, ref)} disabled={removing}>
          <X size={14} />
        </Button>
      )}
    </li>
  );
}

// « Relier une anomalie matérielle » (D-42): an issue of THIS pharmacy, among those the reader can see; it is created in Matériel.
function IssueDialog({ ticket, open, onOpenChange, onLink }) {
  const [issues, setIssues] = useState(null);
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setIssues(null); setChosen(""); setError("");
    api.get("/equipment-issues", { params: { prospect_id: ticket.prospect_id } })
      .then(({ data }) => { if (alive) setIssues(data.items || []); })
      .catch(() => { if (alive) { setIssues([]); setError("Impossible de charger les anomalies de cette pharmacie."); } });
    return () => { alive = false; };
  }, [open, ticket.prospect_id]);
  const linked = new Set((ticket.links?.equipment_issue || []).map((l) => l.id));
  const choices = (issues || []).filter((i) => !linked.has(i.id));
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await onLink("equipment_issue", chosen);
    } catch (e) {
      setError(ticketErrorMessage(e, "Lien impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Relier une anomalie matérielle</DialogTitle>
          <DialogDescription>Les anomalies de {ticket.pharmacy_name} que vous pouvez voir. Une nouvelle anomalie se déclare depuis le matériel de la pharmacie.</DialogDescription>
        </DialogHeader>
        {issues === null ? (
          <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>
        ) : choices.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="ticket-issue-empty">Aucune autre anomalie pour cette pharmacie.</p>
        ) : (
          <ul className="space-y-1 max-h-72 overflow-y-auto" role="radiogroup" aria-label="Anomalies">
            {choices.map((i) => (
              <li key={i.id}>
                <button type="button" role="radio" aria-checked={chosen === i.id} onClick={() => setChosen(i.id)} data-testid={`ticket-issue-${i.id}`}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${chosen === i.id ? "border-jamora bg-jamora/5" : "border-slate-200 hover:bg-slate-50"}`}>
                  <div className="font-medium text-slate-900">{i.number} · {i.title}</div>
                  <div className="text-xs text-slate-500">{[SEVERITY_META[i.severity]?.label, ISSUE_STATUS_META[i.status]?.label].filter(Boolean).join(" · ")}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
        <DialogFooterButtons busy={busy} error={error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!chosen}
          label="Relier" testid="ticket-issue-submit" />
      </DialogContent>
    </Dialog>
  );
}

// « Relier un ticket GitHub » (D-45): the address of an issue or pull request where the fix is followed.
function GithubDialog({ open, onOpenChange, onLink }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setUrl(""); setError(""); } }, [open]);
  const valid = isGithubUrl(url);
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await onLink("github", url.trim());
    } catch (e) {
      setError(ticketErrorMessage(e, "Lien impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Relier un ticket GitHub</DialogTitle>
          <DialogDescription>Le ticket ou la demande de fusion où le correctif est suivi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="ticket-github-url">Adresse</Label>
          <Input id="ticket-github-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://github.com/organisation/depot/issues/123"
            inputMode="url" maxLength={300} data-testid="ticket-github-url" />
          {url.trim() && !valid && <p className="text-xs text-red-600">Une adresse https://github.com/… avec l'organisation et le dépôt.</p>}
        </div>
        <DialogFooterButtons busy={busy} error={error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid}
          label="Relier" testid="ticket-github-submit" />
      </DialogContent>
    </Dialog>
  );
}

// « Noter la satisfaction » (D-44): what the pharmacy said when asked — declared by the agent, never presented as a survey.
export function SatisfactionDialog({ ticket, open, onOpenChange, onDone }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) { setScore(ticket.satisfaction?.score || 0); setComment(ticket.satisfaction?.comment || ""); setError(""); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/satisfaction`,
        { expected_version: ticket.version, score, comment: comment.trim() || null });
      onDone(data);
    } catch (e) {
      setError(ticketErrorMessage(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Noter la satisfaction</DialogTitle>
          <DialogDescription>Ce que la pharmacie a répondu quand vous lui avez demandé si le problème était bien réglé.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Satisfaction">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" role="radio" aria-checked={score === n} aria-label={`${n} — ${SATISFACTION_LABELS[n]}`} onClick={() => setScore(n)}
              data-testid={`ticket-satisfaction-${n}`}
              className={`h-11 rounded-lg border text-sm font-semibold tabular-nums transition-colors ${score === n ? "border-jamora bg-jamora text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              {n}
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-600 min-h-5">{score ? SATISFACTION_LABELS[score] : "Choisissez une note de 1 à 5."}</p>
        <div className="space-y-1.5">
          <Label htmlFor="ticket-satisfaction-comment">Commentaire (facultatif)</Label>
          <Textarea id="ticket-satisfaction-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={500} />
        </div>
        <DialogFooterButtons busy={busy} error={error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!score}
          label="Enregistrer" testid="ticket-satisfaction-submit" />
      </DialogContent>
    </Dialog>
  );
}

// « Lier à un ticket » from an assistance (D-43): an open ticket the reader handles; only the assistance's dates are kept on it.
export function LinkAssistToTicket({ assistId, onLinked }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      api.get("/tickets", { params: { state: "open", limit: 8, ...(query.trim().length >= 2 ? { search: query.trim() } : {}) } })
        .then(({ data }) => { if (alive) setItems(data.items); })
        .catch(() => { if (alive) setItems([]); });
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, query]);
  if (!hasPermission(user, TICKET_HANDLER_KEYS)) return null;
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/tickets/${chosen.id}/links`, { kind: "assist", ref: assistId });
      toast.success(`Assistance reliée au ticket ${chosen.number}`);
      setOpen(false);
      onLinked?.();
    } catch (e) {
      setError(ticketErrorMessage(e, "Lien impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => { setOpen(true); setQuery(""); setChosen(null); setItems(null); setError(""); }}
        data-testid="assist-link-ticket">
        <Link2 size={14} className="mr-1" />Lier à un ticket
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Lier à un ticket</DialogTitle>
            <DialogDescription>Un ticket ouvert que vous traitez. Le ticket garde les dates de l'assistance, jamais ses notes.</DialogDescription>
          </DialogHeader>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Numéro, titre ou pharmacie" aria-label="Rechercher un ticket"
            data-testid="assist-link-ticket-search" />
          {items === null ? (
            <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">Aucun ticket ouvert trouvé.</p>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-y-auto" role="radiogroup" aria-label="Tickets">
              {items.map((t) => (
                <li key={t.id}>
                  <button type="button" role="radio" aria-checked={chosen?.id === t.id} onClick={() => setChosen(t)} data-testid={`assist-link-ticket-${t.number}`}
                    className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${chosen?.id === t.id ? "border-jamora bg-jamora/5" : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className="font-medium text-slate-900 break-words">{t.title}</div>
                    <div className="text-xs text-slate-500">{t.number} · {t.pharmacy_name}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooterButtons busy={busy} error={error} onCancel={() => setOpen(false)} onSubmit={submit} disabled={!chosen}
            label="Relier" testid="assist-link-ticket-submit" />
        </DialogContent>
      </Dialog>
    </>
  );
}

// The ticket's « Liens » card (08-3): what was escalated from it. Each object is created by its own module with its own rights;
// the ticket only links it (same pharmacy), and shows it only to those who may see it.
export default function TicketLinksCard({ ticket, onChanged }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [savingFix, setSavingFix] = useState(false);
  const caps = ticket.capabilities || {};
  const links = ticket.links || {};
  const count = linkCount(links);
  const canTask = hasPermission(user, "tasks:create");
  const canMission = canCreateMission(user);
  const canIssue = canSeeEquipmentPage(user);
  const taskDraft = useMemo(() => ({
    title: `${ticket.number} · ${ticket.title}`.slice(0, 200), domain: "support",
    priority: ["critical", "high", "medium", "low"].includes(ticket.priority) ? ticket.priority : "medium",
    description: `Ticket support ${ticket.number} — ${ticket.pharmacy_name}`,
  }), [ticket.number, ticket.title, ticket.priority, ticket.pharmacy_name]);

  const link = async (kind, ref) => {
    const { data } = await api.post(`/tickets/${ticket.id}/links`, { kind, ref });
    setDialog(null);
    onChanged(data, "Lien ajouté");
  };
  const remove = async (kind, ref) => {
    setRemoving(true);
    try {
      const { data } = await api.delete(`/tickets/${ticket.id}/links/${kind}/${encodeURIComponent(ref)}`);
      onChanged(data, "Lien retiré");
    } catch (e) {
      toast.error(ticketErrorMessage(e, "Impossible de retirer ce lien."));
    } finally {
      setRemoving(false);
    }
  };
  const toggleFix = async (value) => {
    setSavingFix(true);
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}`, { expected_version: ticket.version, awaiting_fix: value });
      onChanged(data, value ? "Correctif attendu" : "Plus de correctif attendu");
    } catch (e) {
      toast.error(ticketErrorMessage(e, "Enregistrement impossible. Réessayez."));
      onChanged(null);
    } finally {
      setSavingFix(false);
    }
  };

  if (!caps.link && count === 0 && !ticket.awaiting_fix) return null;
  const showFix = caps.manage && ticket.status === "resolved";

  return (
    <Card className="p-5 space-y-2" data-testid="ticket-links">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Link2 size={14} className="text-slate-400" />Liens</h2>
        {caps.link && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="ticket-link-add"><Plus size={14} className="mr-1" />Ajouter</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" title="Relier au ticket">
              {canTask && <DropdownMenuItem onClick={() => setTaskOpen(true)} data-testid="ticket-link-task">Créer une tâche de suivi</DropdownMenuItem>}
              {canMission && (
                <DropdownMenuItem onClick={() => navigate(`/missions/nouvelle?prospect=${encodeURIComponent(ticket.prospect_id)}&ticket=${encodeURIComponent(ticket.id)}`)}
                  data-testid="ticket-link-mission">Demander une intervention sur place</DropdownMenuItem>
              )}
              {canIssue && <DropdownMenuItem onClick={() => setDialog("issue")} data-testid="ticket-link-issue">Relier une anomalie matérielle</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setDialog("github")} data-testid="ticket-link-github">Relier un ticket GitHub</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {(showFix || ticket.awaiting_fix) && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
          <Label htmlFor="ticket-awaiting-fix" className="text-sm font-normal text-slate-700">
            Correctif attendu
            <span className="block text-xs text-slate-500">Résolu pour la pharmacie, mais un correctif doit encore être livré.</span>
          </Label>
          <Switch id="ticket-awaiting-fix" checked={!!ticket.awaiting_fix} onCheckedChange={toggleFix} disabled={!showFix || savingFix}
            data-testid="ticket-awaiting-fix" />
        </div>
      )}
      {count === 0 ? (
        <p className="text-sm text-slate-500">Aucune tâche, mission, anomalie ni assistance reliée.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {Object.keys(LINK_KIND_LABELS).flatMap((kind) => (links[kind] || []).map((item) => (
            <LinkRow key={`${kind}:${item.id || item.url}`} kind={kind} item={item} canRemove={!!caps.link} onRemove={remove} removing={removing} />
          )))}
        </ul>
      )}

      {canIssue && <IssueDialog ticket={ticket} open={dialog === "issue"} onOpenChange={(o) => setDialog(o ? "issue" : null)} onLink={link} />}
      <GithubDialog open={dialog === "github"} onOpenChange={(o) => setDialog(o ? "github" : null)} onLink={link} />
      {canTask && (
        <TaskFormSheet open={taskOpen} onOpenChange={setTaskOpen} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false}
          directory={[]} lockedProspect={{ id: ticket.prospect_id, pharmacy_name: ticket.pharmacy_name }} initialValues={taskDraft}
          onSaved={async (task) => {
            setTaskOpen(false);
            try {
              await link("task", task.id);
            } catch (e) {
              toast.error(ticketErrorMessage(e, "La tâche est créée mais n'a pas pu être reliée au ticket."));
            }
          }} />
      )}
    </Card>
  );
}
